export const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "real_estate", label: "Real Estate" },
  { value: "weather", label: "Weather" },
  { value: "food", label: "Food" },
  { value: "transport", label: "Transport" },
  { value: "culture", label: "Culture" },
  { value: "vendor_directory", label: "Vendors" },
  { value: "vehicle_market", label: "Vehicle Market" },
  { value: "finance", label: "Finance" },
  { value: "analytics", label: "Analytics" },
];

function isRealEstateEnabled(source) {
  return Boolean(source.enabled);
}

function isWeatherEnabled(source) {
  return Boolean(source.isActive);
}

function isRealEstateRunning(source) {
  return (
    source.isRunning ||
    source.health === "running" ||
    source.latestRun?.status === "RUNNING" ||
    source.latestRun?.status === "QUEUED"
  );
}

function isWeatherRunning(source) {
  return source.isRunning || source.health === "running";
}

function isRealEstateFailed(source) {
  return source.health === "failed" || source.latestRun?.status === "FAILED";
}

function isWeatherFailed(source) {
  return source.health === "failed" || source.lastStatus === "failed";
}

function isRealEstateHealthy(source) {
  return source.enabled && source.health === "healthy";
}

function isWeatherHealthy(source) {
  return source.isActive && source.health === "healthy";
}

function isDomainSourceEnabled(source) {
  return Boolean(source.isActive ?? source.enabled);
}

function isDomainSourceRunning(source) {
  return (
    source.isRunning ||
    source.health === "running" ||
    source.healthStatus === "running" ||
    source.lastStatus === "running" ||
    source.latestRunStatus === "running"
  );
}

function isDomainSourceFailed(source) {
  return source.healthStatus === "failed" || source.healthStatus === "failing";
}

function isDomainSourceHealthy(source) {
  const active = source.isActive ?? source.enabled;
  return active && (source.health === "healthy" || source.healthStatus === "healthy");
}

function isDomainSourceCollecting(source) {
  return Boolean(source.isCollecting) || (source.latestRecordsReceived ?? 0) > 0;
}

function isVehicleMarketEnabled(row) {
  if (row?.isActive === false) return false;
  if (row?.isActive === true) return true;
  return row?.registry !== "vehicle_only";
}

function isVehicleMarketRunning(row) {
  return (
    row?.isRunning ||
    row?.healthStatus === "running" ||
    row?.lastRunStatus === "running"
  );
}

function isVehicleMarketFailed(row) {
  return row?.healthStatus === "failed" || row?.lastRunStatus === "failed";
}

function isVehicleMarketHealthy(row) {
  if (!isVehicleMarketEnabled(row)) return false;
  if (row?.complianceBlocker && row.complianceBlocker !== "none") return false;
  return row?.healthStatus === "healthy";
}

function isVehicleMarketCollecting(row) {
  return (row?.lastRecordCount ?? 0) > 0 || (row?.activeListingCount ?? 0) > 0;
}

function isFinanceEnabled(source) {
  return Boolean(source?.isActive ?? source?.enabled ?? true);
}

function isFinanceRunning(source) {
  return Boolean(source?.isRunning) || source?.health === "running" || source?.healthStatus === "running";
}

function isFinanceFailed(source) {
  return source?.health === "failed" || source?.healthStatus === "failed";
}

function isFinanceHealthy(source) {
  return isFinanceEnabled(source) && (source?.health === "healthy" || source?.healthStatus === "healthy");
}

function isGa4Enabled(source) {
  return Boolean(source?.enabled);
}

function isGa4Running(source) {
  return source?.isRunning || source?.health === "running";
}

function isGa4Failed(source) {
  return source?.health === "failed";
}

function isGa4Healthy(source) {
  return isGa4Enabled(source) && source?.health === "healthy";
}

export function buildTypeCounts(
  realEstateSources,
  weatherSources,
  domainSources = {},
  vehicleMarketCount = 0,
  financeSources = [],
  ga4Sources = []
) {
  const food = domainSources.food || [];
  const transport = domainSources.transport || [];
  const culture = domainSources.culture || [];
  const vendorDirectory = domainSources.vendor_directory || [];
  const financeCount = financeSources.length;
  const analyticsCount = ga4Sources.length;

  return {
    all:
      realEstateSources.length +
      weatherSources.length +
      food.length +
      transport.length +
      culture.length +
      vendorDirectory.length +
      vehicleMarketCount +
      financeCount +
      analyticsCount,
    real_estate: realEstateSources.length,
    weather: weatherSources.length,
    food: food.length,
    transport: transport.length,
    culture: culture.length,
    vendor_directory: vendorDirectory.length,
    vehicle_market: vehicleMarketCount,
    finance: financeCount,
    analytics: analyticsCount,
  };
}

export function buildSourceSummary(
  realEstateSources,
  weatherSources,
  typeFilter = "all",
  domainSources = {},
  vehicleMarketSources = [],
  financeSources = [],
  ga4Sources = []
) {
  const includeRealEstate = typeFilter === "all" || typeFilter === "real_estate";
  const includeWeather = typeFilter === "all" || typeFilter === "weather";
  const includeFood = typeFilter === "all" || typeFilter === "food";
  const includeTransport = typeFilter === "all" || typeFilter === "transport";
  const includeCulture = typeFilter === "all" || typeFilter === "culture";
  const includeVendorDirectory = typeFilter === "all" || typeFilter === "vendor_directory";
  const includeVehicleMarket = typeFilter === "all" || typeFilter === "vehicle_market";
  const includeFinance = typeFilter === "all" || typeFilter === "finance";
  const includeAnalytics = typeFilter === "all" || typeFilter === "analytics";

  const reList = includeRealEstate ? realEstateSources : [];
  const weatherList = includeWeather ? weatherSources : [];
  const foodList = includeFood ? domainSources.food || [] : [];
  const transportList = includeTransport ? domainSources.transport || [] : [];
  const cultureList = includeCulture ? domainSources.culture || [] : [];
  const vendorDirectoryList = includeVendorDirectory
    ? domainSources.vendor_directory || []
    : [];
  const vehicleMarketList = includeVehicleMarket ? vehicleMarketSources : [];
  const financeList = includeFinance ? financeSources : [];
  const ga4List = includeAnalytics ? ga4Sources : [];

  const domainLists = [...foodList, ...transportList, ...cultureList, ...vendorDirectoryList];

  const total =
    reList.length +
    weatherList.length +
    domainLists.length +
    vehicleMarketList.length +
    financeList.length +
    ga4List.length;
  const enabled =
    reList.filter(isRealEstateEnabled).length +
    weatherList.filter(isWeatherEnabled).length +
    domainLists.filter(isDomainSourceEnabled).length +
    vehicleMarketList.filter(isVehicleMarketEnabled).length +
    financeList.filter(isFinanceEnabled).length +
    ga4List.filter(isGa4Enabled).length;
  const disabled = total - enabled;
  const running =
    reList.filter(isRealEstateRunning).length +
    weatherList.filter(isWeatherRunning).length +
    domainLists.filter(isDomainSourceRunning).length +
    vehicleMarketList.filter(isVehicleMarketRunning).length +
    financeList.filter(isFinanceRunning).length +
    ga4List.filter(isGa4Running).length;
  const scheduled =
    reList.filter((source) => source.scheduleEnabled).length +
    weatherList.filter((source) => source.scheduleEnabled).length +
    foodList.filter((source) => source.scheduleEnabled).length +
    transportList.filter((source) => source.scheduleEnabled).length +
    cultureList.filter((source) => source.scheduleEnabled).length +
    vendorDirectoryList.filter((source) => source.scheduleEnabled).length +
    vehicleMarketList.filter(
      (row) => row.capabilities?.supportsSchedule && row.scheduleEnabled
    ).length +
    financeList.filter((source) => source.scheduleEnabled).length +
    ga4List.filter((source) => source.enabled && source.pollIntervalMinutes).length;
  const failed =
    reList.filter(isRealEstateFailed).length +
    weatherList.filter(isWeatherFailed).length +
    domainLists.filter(isDomainSourceFailed).length +
    vehicleMarketList.filter(isVehicleMarketFailed).length +
    financeList.filter(isFinanceFailed).length +
    ga4List.filter(isGa4Failed).length;
  const healthy =
    reList.filter(isRealEstateHealthy).length +
    weatherList.filter(isWeatherHealthy).length +
    domainLists.filter(isDomainSourceHealthy).length +
    vehicleMarketList.filter(isVehicleMarketHealthy).length +
    financeList.filter(isFinanceHealthy).length +
    ga4List.filter(isGa4Healthy).length;
  const collecting =
    domainLists.filter(isDomainSourceCollecting).length +
    vehicleMarketList.filter(isVehicleMarketCollecting).length;

  return { total, enabled, disabled, running, scheduled, failed, healthy, collecting };
}
